/**
 * "Issue Identified" on a pilot test (Bill 2026-11-04): the same life-ring report dialog every screen has, opened
 * prefilled with the test's ID and what it tests. Sending it files the report exactly as the lifebuoy does (POST
 * /v3/events, support.report: the Teams card, the Change Tracker item); then the report is tied to the test
 * (POST /v3/rop/pilot/items/{id}/issues), which marks the item Issue Identified (or Fail for a scenario) and shows its
 * CHG number once the tracker has filed it.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SupportReportDialog, captureScreen, type ScreenPicture } from '@retail-ops/support-report';
import { useRopSupportHost } from '@/components/support/SupportReportButton';
import type { PilotItem, PilotSection } from './pilotApi';
import { issuePrefill, reportIdFrom } from './pilotModel';

export interface PilotIssueDialogProps {
  item: PilotItem | null;
  section: PilotSection | undefined;
  onClose: () => void;
  /** The report was sent; reportId is null when the server did not return one. */
  onSent: (reportId: string | null) => void;
}

export function PilotIssueDialog({ item, section, onClose, onSent }: PilotIssueDialogProps): JSX.Element | null {
  const open = item !== null;
  const host = useRopSupportHost(open);
  const [picture, setPicture] = useState<ScreenPicture | null | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPicture(undefined);
    void captureScreen().then((p) => { if (!cancelled) setPicture(p); });
    return () => { cancelled = true; };
  }, [open]);

  if (!item || typeof document === 'undefined') return null;
  const pre = issuePrefill(item, section);
  return createPortal(
    <SupportReportDialog
      {...host}
      open={open}
      onClose={onClose}
      picture={picture}
      initial={{ kind: pre.kind, attempting: pre.attempting, expected: pre.expected, context: pre.context }}
      onSent={(result) => onSent(reportIdFrom(result))}
    />,
    document.body,
  );
}
